import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AVAILS, IconLocalMappings, PlanRow } from '@blisspointmedia/bpm-types/dist/LinearBuying';
import {
  awaitJSON,
  LinearBuyingLambdaFetch,
  LinearLambdaFetch,
  LinearOptimizationsLambdaFetch
} from '@src/utils/fetch-utils';
import { convert24hrTo12hr } from '@src/utils/time-utils';
import { DAY_OF_WEEK_MAP, LINEAR_RATE_STATUSES } from './linear-buying-constants';
import {
  Constraints,
  convertDaysOfWeekStringToArray,
  NielsenEstimates,
  RotationPricing,
  RotationsAndPricing
} from './linear-buying-utils';

export interface Campaign {
  id: number;
  campaign: string;
}

export interface GetPlansRes {
  rows: PlanRow[];
  campaigns: Campaign[];
  assumedClearanceRate: number;
}

export interface NielsenEstimatesRes {
  estimatesByHH: NielsenEstimates;
  universeEstimate: number;
}

/** A single row of `/get_rates`, before it's folded into the network/avail lookup. */
interface LinearRateRow {
  network: string;
  avail: (typeof AVAILS)[number];
  cost_30s: number;
  rotation_name: string;
  day_set: string;
  daypart_begin: string;
  daypart_end: string;
  status: string;
  market: string | null;
}

interface ConstraintsRes {
  constraints: Record<string, { network: string }[]>;
}

export const fetchLinearPlans = async (
  company: string,
  weekStart: string,
  weekEnd: string,
  channel: string
): Promise<GetPlansRes> => {
  const res = await LinearBuyingLambdaFetch('/get_linear_plans', {
    params: { company, weekStart, weekEnd, channel }
  });
  return awaitJSON<GetPlansRes>(res);
};

export const useLinearPlans = (
  company: string,
  weekStart: string,
  weekEnd: string,
  channel: string
): ReturnType<typeof useQuery<GetPlansRes>> =>
  useQuery({
    queryKey: LINEAR_PLANS_QUERY_KEY(company, weekStart, weekEnd, channel),
    enabled: Boolean(company && weekStart && weekEnd),
    queryFn: () => fetchLinearPlans(company, weekStart, weekEnd, channel)
  });

export const LINEAR_PLANS_QUERY_KEY = (
  company?: string,
  weekStart?: string,
  weekEnd?: string,
  channel?: string
): unknown[] => ['linearBuyingPlans', company, weekStart, weekEnd, channel].filter(Boolean);

/** `demos` is the comma-joined list of selected demo keys, matching the lambda's param format. */
export const useNielsenEstimates = (
  demos: string,
  measurement: string
): ReturnType<typeof useQuery<NielsenEstimatesRes>> =>
  useQuery({
    queryKey: ['linearBuyingNielsenEstimates', demos, measurement],
    enabled: Boolean(demos && measurement),
    // This lambda is slow, and the estimates behind a given demos/measurement pair are effectively
    // static reference data, so cache them well past the app-wide 5/10 minute defaults — switching
    // back to a demo set you've already looked at should be instant.
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    // Changing demos mints a new cache key, which would otherwise blank `data` and make the
    // audience columns compute against an empty estimates map. Hold the previous demo's numbers
    // on screen until the new ones arrive; the column headers show that they're being refreshed.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await LinearBuyingLambdaFetch('/getNielsenEstimates', {
        params: { demos, measurement }
      });
      return awaitJSON<NielsenEstimatesRes>(res);
    }
  });

export const useLinearNetworks = (channel: string): ReturnType<typeof useQuery<string[]>> =>
  useQuery({
    queryKey: ['linearBuyingNetworks', channel],
    queryFn: async () => {
      const res = await LinearBuyingLambdaFetch('/networks', { params: { channel } });
      const networksMap = await awaitJSON<Record<string, unknown>>(res);
      return Object.keys(networksMap);
    }
  });

/**
 * Folds `/get_rates` into a network -> avail -> rotations lookup, dropping any rate whose status
 * isn't buyable/secured/general or whose network isn't carried on this channel.
 */
export const useLinearRates = (
  company: string,
  networks: string[] | undefined
): ReturnType<typeof useQuery<RotationsAndPricing>> =>
  useQuery({
    queryKey: ['linearBuyingRates', company, networks],
    enabled: Boolean(company && networks),
    queryFn: async () => {
      const res = await LinearLambdaFetch(`/get_rates?company=${company}`);
      const ratesData = await awaitJSON<LinearRateRow[]>(res);

      const ratesMap: RotationsAndPricing = {};
      for (const row of ratesData) {
        if (!LINEAR_RATE_STATUSES.includes(row.status) || !networks?.includes(row.network)) {
          continue;
        }
        const {
          network,
          avail,
          cost_30s,
          rotation_name,
          day_set,
          daypart_begin,
          daypart_end,
          status,
          market
        } = row;

        const daypart = `${convert24hrTo12hr(daypart_begin)}-${convert24hrTo12hr(daypart_end)}`;
        const dowString = DAY_OF_WEEK_MAP[day_set] || 'MTuWThFSaSu';

        if (!ratesMap[network]) {
          ratesMap[network] = {};
        }
        const rotation: RotationPricing = {
          network,
          avail,
          cost30s: cost_30s,
          rotationName: rotation_name,
          daypartBegin: daypart_begin,
          daypartEnd: daypart_end,
          daysOfWeek: convertDaysOfWeekStringToArray(dowString),
          rotationLabel: `${rotation_name} (${day_set} ${daypart})`,
          status,
          market
        };
        ratesMap[network][avail] = [...(ratesMap[network][avail] || []), rotation];
      }
      return ratesMap;
    }
  });

export const useLinearConstraints = (): ReturnType<typeof useQuery<Constraints>> =>
  useQuery({
    queryKey: ['linearBuyingConstraints'],
    queryFn: async () => {
      const res = await LinearOptimizationsLambdaFetch('/constraints?company=global&name=global');
      const data = await awaitJSON<ConstraintsRes>(res);
      const constraints: Constraints = { maximum: {}, avoid: {} };
      for (const category of ['maximum', 'avoid'] as const) {
        for (const row of data.constraints[category]) {
          constraints[category][row.network] = [...(constraints[category][row.network] || []), row];
        }
      }
      return constraints;
    }
  });

/**
 * Future weeks whose plan row matches `row` on every buy attribute, so a single edit can be
 * extended across all of them.
 */
export const useFutureMatchingPlans = (
  company: string,
  row: PlanRow | undefined
): ReturnType<typeof useQuery<PlanRow[]>> =>
  useQuery({
    queryKey: ['linearBuyingFutureMatchingPlans', company, row?.key],
    enabled: Boolean(company && row),
    queryFn: async () => {
      const {
        week,
        network,
        daypart_start,
        daypart_end,
        dow,
        avail,
        length,
        type,
        cost,
        notes,
        campaign_id,
        market
      } = row!;
      // Need to convert before it's used as a param. This matches the format for querying the DB.
      const stringifiedDow = JSON.stringify(dow)
        .replace('[', '{')
        .replace(']', '}')
        .replaceAll('"', '');
      const res = await LinearBuyingLambdaFetch('/get_future_matching_plans', {
        params: {
          company,
          weekStart: week,
          network,
          daypart_start,
          daypart_end,
          dow: stringifiedDow,
          avail,
          length,
          type,
          cost,
          notes,
          campaign_id,
          market
        }
      });
      return awaitJSON<PlanRow[]>(res);
    }
  });

export interface SaveLinearPlansParams {
  editedRows: PlanRow[];
  newRows: PlanRow[];
  company: string;
  useRadioExperiment: boolean;
  useLinearBuyingUpdatesExperiment: boolean | string;
}

export const saveLinearPlans = async (body: SaveLinearPlansParams): Promise<void> => {
  await LinearBuyingLambdaFetch('/update_linear_plans', { method: 'POST', body });
};

export const fetchIconLocalMappings = async (): Promise<IconLocalMappings> => {
  const res = await LinearBuyingLambdaFetch('/icon_local_mappings');
  return awaitJSON<IconLocalMappings>(res);
};

export interface NmiExportParams {
  company: string;
  demos: string;
  measurement: string;
  weekStart: string;
  weekEnd: string;
  assumedClearanceRate?: number;
}

export const fetchBookedNielsenEstimates = async ({
  company,
  demos,
  measurement,
  weekStart,
  assumedClearanceRate
}: NmiExportParams): Promise<Record<string, unknown>[]> => {
  const res = await LinearBuyingLambdaFetch('/getBookedNeilsenEstimates', {
    params: { company, week: weekStart, demos, measurement, assumedClearanceRate }
  });
  return awaitJSON<Record<string, unknown>[]>(res);
};

export const fetchDeliveredNielsenEstimates = async ({
  company,
  demos,
  measurement,
  weekStart,
  weekEnd
}: NmiExportParams): Promise<Record<string, unknown>[]> => {
  const res = await LinearBuyingLambdaFetch('/getDeliveredNielsenEstimates', {
    params: { demos, measurement, weekStart, weekEnd, company }
  });
  return awaitJSON<Record<string, unknown>[]>(res);
};
